import { useState, type FormEvent } from 'react'
import { useTranslation } from 'react-i18next'
import { Send, CheckCircle, AlertCircle } from 'lucide-react'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { Textarea } from '@/components/ui/Textarea'

interface FormData {
  name: string
  email: string
  subject: string
  message: string
  privacy: boolean
  website: string   // honeypot
}

interface FormErrors {
  name?: string
  email?: string
  subject?: string
  message?: string
  privacy?: string
}

type FormStatus = 'idle' | 'submitting' | 'success' | 'error'

export function ContactForm() {
  const { t } = useTranslation()
  const [formData, setFormData] = useState<FormData>({
    name: '',
    email: '',
    subject: '',
    message: '',
    privacy: false,
    website: '',
  })
  const [errors, setErrors] = useState<FormErrors>({})
  const [status, setStatus] = useState<FormStatus>('idle')
  const [errorKey, setErrorKey] = useState<string>('contact.errors.serverError')

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {}

    if (!formData.name.trim()) {
      newErrors.name = t('contact.errors.nameRequired')
    }

    if (!formData.email.trim()) {
      newErrors.email = t('contact.errors.emailRequired')
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = t('contact.errors.emailInvalid')
    }

    if (!formData.subject.trim()) {
      newErrors.subject = t('contact.errors.subjectRequired')
    }

    if (!formData.message.trim()) {
      newErrors.message = t('contact.errors.messageRequired')
    }

    if (!formData.privacy) {
      newErrors.privacy = t('contact.errors.privacyRequired')
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()
    if (!validateForm()) return

    setStatus('submitting')
    setErrorKey('contact.errors.serverError')

    try {
      const res = await fetch('/api/send-email', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ type: 'contact', ...formData }),
      })

      if (!res.ok) {
        const data = await res.json().catch(() => ({}))
        setErrorKey(
          data?.error === 'rate_limited'
            ? 'contact.errors.rateLimited'
            : 'contact.errors.serverError'
        )
        setStatus('error')
        return
      }

      setStatus('success')
      setFormData({
        name: '', email: '', subject: '', message: '', privacy: false, website: '',
      })
    } catch {
      setStatus('error')
    }
  }

  const handleChange = (field: keyof FormData, value: string | boolean) => {
    setFormData((prev) => ({ ...prev, [field]: value }))
    if (errors[field as keyof FormErrors]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }))
    }
  }

  if (status === 'success') {
    return (
      <div className="rounded-lg bg-success-50 p-8 text-center">
        <CheckCircle className="mx-auto h-12 w-12 text-success" />
        <h3 className="mt-4 text-xl font-semibold text-secondary-900">
          {t('contact.successTitle')}
        </h3>
        <p className="mt-2 text-secondary-600">
          {t('contact.successMessage')}
        </p>
        <Button
          variant="primary"
          className="mt-6"
          onClick={() => setStatus('idle')}
        >
          {t('contact.sendAnother')}
        </Button>
      </div>
    )
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <input
        type="text"
        name="website"
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
        value={formData.website}
        onChange={(e) => handleChange('website', e.target.value)}
        style={{ position: 'absolute', left: '-9999px', width: '1px', height: '1px', opacity: 0 }}
      />
      {status === 'error' && (
        <div className="flex items-center gap-3 rounded-lg bg-error-50 p-4 text-error-700">
          <AlertCircle className="h-5 w-5 flex-shrink-0" />
          <p>{t(errorKey)}</p>
        </div>
      )}

      <div className="grid gap-6 sm:grid-cols-2">
        <Input
          label={t('contact.name')}
          value={formData.name}
          onChange={(e) => handleChange('name', e.target.value)}
          error={errors.name}
          required
          disabled={status === 'submitting'}
        />
        <Input
          label={t('contact.email')}
          type="email"
          value={formData.email}
          onChange={(e) => handleChange('email', e.target.value)}
          error={errors.email}
          required
          disabled={status === 'submitting'}
        />
      </div>

      <Input
        label={t('contact.subject')}
        value={formData.subject}
        onChange={(e) => handleChange('subject', e.target.value)}
        error={errors.subject}
        required
        disabled={status === 'submitting'}
      />

      <Textarea
        label={t('contact.message')}
        value={formData.message}
        onChange={(e) => handleChange('message', e.target.value)}
        error={errors.message}
        required
        disabled={status === 'submitting'}
        rows={5}
      />

      <div className="flex items-start gap-3">
        <input
          type="checkbox"
          id="privacy"
          checked={formData.privacy}
          onChange={(e) => handleChange('privacy', e.target.checked)}
          className="mt-1 h-4 w-4 rounded border-secondary-300 text-primary focus:ring-primary"
          disabled={status === 'submitting'}
        />
        <div>
          <label htmlFor="privacy" className="text-sm text-secondary-700">
            {t('contact.privacyConsent')}{' '}
            <a href="/privacy" className="text-primary hover:underline">
              {t('contact.privacyPolicy')}
            </a>
            <span className="ml-1 text-error">*</span>
          </label>
          {errors.privacy && (
            <p className="mt-1 text-sm text-error">{errors.privacy}</p>
          )}
        </div>
      </div>

      <Button
        type="submit"
        variant="primary"
        size="lg"
        isLoading={status === 'submitting'}
        className="w-full sm:w-auto"
      >
        <Send className="mr-2 h-4 w-4" />
        {t('buttons.send')}
      </Button>
    </form>
  )
}
